import { AsyncProcessRepository } from '../Repositories/AsyncProcessRepository';
import { AsyncProcessStatusItemStatusEnum } from '../axios/generated/sqsAccessManage/api';

/** ポーリング設定 */
export interface AsyncPollingOptions {
  /** ポーリング間隔(ms) デフォルト: 3000 */
  intervalMs?: number;
  /** 最大待機時間(ms) デフォルト: 300000 (5分) */
  maxWaitMs?: number;
  /** actionTypeフィルタ (例: 'single-simulation') */
  actionType?: string;
  /** 中断用のAbortSignal。画面遷移時などにポーリングをキャンセルできる */
  signal?: AbortSignal;
}

/** ポーリング結果 */
export interface AsyncPollingResult<T = object> {
  /** 処理結果（completed時） */
  result?: T;
  /** エラーメッセージ（failed時） */
  errorMessage?: string;
  /** 最終ステータス */
  status: 'completed' | 'failed';
  /** リクエストID */
  requestId: string;
}

const DEFAULT_INTERVAL_MS = 3000;
const DEFAULT_MAX_WAIT_MS = 300000;

/**
 * requestIdとactionTypeを指定してDynamoDBをポーリングし、処理完了またはタイムアウトまで待機する。
 * completed/failedになったらDynamoDBのステータスを削除して結果を返す。
 *
 * 内部は再帰setTimeoutパターンで実装しており、前回のpollが完了してからintervalMs後に次のpollを開始する。
 * これにより、async競合（C-3）・未処理例外（C-1）・初回Promise未ハンドリング（C-2）を全て防止する。
 * @param requestId - ポーリング対象のリクエストID
 * @param options - ポーリング設定
 * @returns 処理結果のPromise
 * @throws タイムアウト時・キャンセル時にErrorをthrow
 */
export const pollAsyncProcess = <T = object>(requestId: string, options?: AsyncPollingOptions): Promise<AsyncPollingResult<T>> => {
  const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;
  const maxWaitMs = options?.maxWaitMs ?? DEFAULT_MAX_WAIT_MS;
  const actionType = options?.actionType;
  const signal = options?.signal;
  const repository = new AsyncProcessRepository();

  return new Promise<AsyncPollingResult<T>>((resolve, reject) => {
    const startTime = Date.now();
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    /** タイマーをクリアする */
    const cleanup = () => {
      if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
      }
    };

    /**
     * Promiseを一度だけresolve/rejectする
     * @param fn - 実行する関数
     */
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    // AbortSignalによるキャンセル
    if (signal) {
      if (signal.aborted) {
        reject(new Error(`ポーリングがキャンセルされました (requestId: ${requestId})`));
        return;
      }
      signal.addEventListener(
        'abort',
        () => {
          settle(() => reject(new Error(`ポーリングがキャンセルされました (requestId: ${requestId})`)));
        },
        { once: true }
      );
    }

    /**
     * DynamoDBステータスをベストエフォートで削除する
     * @param targetRequestId - 削除対象のリクエストID
     */
    const deleteStatusBestEffort = (targetRequestId: string) => {
      repository.deleteStatus(targetRequestId).catch((err) => {
        console.warn(`DynamoDBステータス削除失敗 (requestId: ${targetRequestId}):`, err);
      });
    };

    /** ステータスを1回ポーリングする */
    const poll = async () => {
      if (settled) return;

      if (Date.now() - startTime > maxWaitMs) {
        settle(() => reject(new Error(`非同期処理がタイムアウトしました (requestId: ${requestId}, ${maxWaitMs}ms超過)`)));
        return;
      }

      try {
        const response = await repository.getStatus(actionType);

        if (settled) return;

        if (response.status.type === 'error') {
          const errorStatus = response.status;
          settle(() => reject(new Error(`ポーリングAPIエラー: ${errorStatus.error.message}`)));
          return;
        }

        const items = response.status.data;
        const target = items.find((item) => item.requestId === requestId);
        if (!target) {
          // まだDynamoDBに反映されていない可能性がある→次回ポーリングで再試行
          scheduleNext();
          return;
        }

        if (target.status === AsyncProcessStatusItemStatusEnum.Completed) {
          deleteStatusBestEffort(requestId);
          settle(() =>
            resolve({
              result: target.result as T | undefined,
              status: 'completed',
              requestId: target.requestId,
            })
          );
          return;
        }

        if (target.status === AsyncProcessStatusItemStatusEnum.Failed) {
          deleteStatusBestEffort(requestId);
          settle(() =>
            resolve({
              errorMessage: target.errorMessage,
              status: 'failed',
              requestId: target.requestId,
            })
          );
          return;
        }

        // pending / processing → 次回ポーリングをスケジュール
        scheduleNext();
      } catch (err) {
        // customAxiosInstanceの例外（refreshToken expired等）もここで捕捉
        settle(() => reject(err instanceof Error ? err : new Error(String(err))));
      }
    };

    /** 前回のpoll完了後にintervalMs待ってから次のpollを実行する */
    const scheduleNext = () => {
      if (settled) return;
      timerId = setTimeout(poll, intervalMs);
    };

    // 初回は即座にポーリング（awaitせずPromiseチェーンで例外を捕捉）
    poll().catch((err) => {
      settle(() => reject(err instanceof Error ? err : new Error(String(err))));
    });
  });
};
